'use client'

import React, { useState, useEffect } from 'react';
import styles from '../styles/PurchaseFlow.module.css';

interface ReferralCodeInputProps {
  onPrevious: () => void;
  onNext: (referralCode: string | null) => void;
  initialValue: string | null;
}

export const ReferralCodeInput = ({ onPrevious, onNext, initialValue }: ReferralCodeInputProps) => {
  const [referralCode, setReferralCode] = useState(initialValue || '');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  // Reset validation state when input changes
  useEffect(() => {
    setIsValid(null);
    setError(null);
  }, [referralCode]);

  const validateReferralCode = async () => {
    if (!referralCode || referralCode.trim() === '') {
      // Skip validation if empty - it's optional
      return true;
    }

    try {
      setIsValidating(true);
      setError(null);
      
      const response = await fetch(`/api/admin/referrals?code=${encodeURIComponent(referralCode.trim())}`, {
        method: 'HEAD'
      });
      
      if (response.ok) {
        setIsValid(true);
        return true;
      } else if (response.status === 404) {
        setIsValid(false);
        setError('Invalid referral code. Please check and try again.');
        return false;
      } else {
        throw new Error(`Server returned ${response.status}`);
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      setIsValid(false);
      setError('Could not validate referral code. Please try again.');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (referralCode.trim() === '') {
      // If no code is entered, just proceed (it's optional)
      onNext(null);
      return;
    }
    
    const isCodeValid = await validateReferralCode();
    if (isCodeValid) {
      onNext(referralCode.trim());
    }
  };

  const handleSkip = () => {
    onNext(null);
  };

  return (
    <div className={styles.activeStepContent}>
      <button onClick={onPrevious} className={styles.backButton}>
        ← Back
      </button>
      
      <h3 className={styles.stepTitle}>Enter Referral Code (Optional)</h3>
      
      <p className={styles.stepDescription}>
        If you have a referral code, enter it below. This step is optional.
      </p>
      
      <form onSubmit={handleSubmit} className={styles.formContainer}>
        <div className={styles.formGroup}>
          <label htmlFor="referralCode" className={styles.inputLabel}>Referral Code:</label>
          <input
            type="text"
            id="referralCode"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            placeholder="Enter referral code (optional)"
            disabled={isValidating}
            className={`${styles.input} ${isValid === false ? styles.errorInput : ''} ${isValid === true ? styles.successInput : ''}`}
          />
          {error && <div className={styles.errorMessage}>{error}</div>}
          {isValid === true && <div className={styles.successMessage}>Valid referral code!</div>}
        </div>
        
        <div className={styles.buttonContainer}>
          
          
          <button 
            type="submit" 
            className={styles.button}
            disabled={isValidating || isValid === false}
          >
            {isValidating ? (
              <>
                <span className={styles.spinner}></span>
                Validating...
              </>
            ) : (
              referralCode.trim() === '' ? 'Skip' : 'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}; 